import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu } from '@mui/material';
import useOnClickOutside from 'hooks/clickOutside';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { Color } from 'shared/utils/colors';
import useIsMobile from '../../../../hooks/useIsMobile';
import { ActionsType } from '../types';
import { desktopActionsSx, mobileContainerSx } from './styles';
import { RotateButton } from './rotateButton';
import { DrawButton } from './drawButton';
import { DownloadButton } from './downloadButton';
import { RemoveButton } from './removeButton';
import { EditButton } from './editButton';
import { ShowIssuesButton } from './showIssuesButton';
import { FilterButton } from './filterButton';
import { IssueStateToggleButton } from './issueStateToggleButton';
import { ExportToDocumentationButton } from './exportToDocumentationButton';
import { GoToSourceIssueButton } from './goToSourceIssueButton';
import { GoToDocumentationFileButton } from './goToDocumentationFileButton';
import { ShareLinkButton } from './shareLinkButton';
import { UnlinkDocumentationButton } from './unlinkDocumentationButton';
import { RefreshPreviewButton } from './refreshPreviewButton';
import { GalleryAction, useGalleryContext } from '..';

interface CustomEditComponentProps {
  file: {
    title: string;
    description?: string | null | undefined;
    localId?: number;
  };
  close: () => void;
}
interface Props {
  className?: string;
  idx: number;
  actions: ActionsType;
  setDrawMode: (bool: boolean) => void;
  disabled: boolean;
  EditComponent?: (props: CustomEditComponentProps) => ReactElement;
  titleId?: string;
}

const Actions: React.FC<Props> = ({
  idx,
  actions,
  setDrawMode,
  disabled,
}) => {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const ref = useRef(null);
  const { showActions, fetchShareableUrl } = useGalleryContext();

  const handleClickOutside = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  useOnClickOutside(ref, handleClickOutside);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (disabled) {
    return null;
  }
  const showDrawButton = showActions?.includes(GalleryAction.DRAW);
  const showRotateButton = showActions?.includes(GalleryAction.ROTATE);
  const showEditButton = showActions?.includes(GalleryAction.EDIT);
  const showDownloadButton = showActions?.includes(GalleryAction.DOWNLOAD);
  const showRefreshPreviewButton = showActions?.includes(
    GalleryAction.REFRESH_PREVIEW
  );
  const showRemoveButton = showActions?.includes(GalleryAction.REMOVE);
  const showShowIssuesButton = showActions?.includes(
    GalleryAction.SHOW_ISSUES
  );
  const showFiltersButton = showActions?.includes(GalleryAction.FILTERS);
  const showExportToDocumentationButton = showActions?.includes(
    GalleryAction.EXPORT_TO_DOCUMENTATION
  );
  const showGoToSourceIssueButton = showActions?.includes(
    GalleryAction.GO_TO_SOURCE_ISSUE
  );
  const showGoToDocumentationFileButton = showActions?.includes(
    GalleryAction.GO_TO_DOCUMENTATION_FILE
  );
  const showShareLinkButton =
    showActions?.includes(GalleryAction.SHARE_LINK) &&
    Boolean(fetchShareableUrl);
    
  const showUnlinkDocumentationButton = showActions?.includes(
    GalleryAction.UNLINK_LINKED_DOCUMENTATION
  );

  return isMobile ? (
    <Box sx={mobileContainerSx} ref={ref}>
      <Box>
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            color: Color.white,
            padding: 1.25,
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPaper-root': {
              minWidth: 200,
              mt: 1,
            },
          }}
        >
          {showRefreshPreviewButton && (
            <RefreshPreviewButton isMobile={true} />
          )}
          {showShowIssuesButton && <ShowIssuesButton isMobile={true} />}
          {showDrawButton && (
            <DrawButton
              setDrawMode={setDrawMode}
              isMobile={true}
              onMenuClose={handleMenuClose}
            />
          )}
          {showEditButton && (
            <EditButton isMobile={true} onMenuClose={handleMenuClose} />
          )}
          {showDownloadButton && (
            <DownloadButton idx={idx} isMobile={true} />
          )}
          {showUnlinkDocumentationButton && (
            <UnlinkDocumentationButton
              idx={idx}
              actions={actions}
              isMobile={true}
              onMenuClose={handleMenuClose}
            />
          )}
          {showRotateButton && (
            <RotateButton idx={idx} actions={actions} isMobile={true} />
          )}
          {showRemoveButton && (
            <RemoveButton
              idx={idx}
              actions={actions}
              isMobile={true}
              onMenuClose={handleMenuClose}
            />
          )}
          {showGoToSourceIssueButton && (
            <GoToSourceIssueButton isMobile={true} />
          )}
          {showFiltersButton && (
            <IssueStateToggleButton isMobile={true} />
          )}
          {showFiltersButton && <FilterButton isMobile={true} />}
          {showExportToDocumentationButton && (
            <ExportToDocumentationButton isMobile={true} />
          )}
          {showGoToDocumentationFileButton && (
            <GoToDocumentationFileButton isMobile={true} />
          )}
          {showShareLinkButton && <ShareLinkButton isMobile={true} />}
        </Menu>
      </Box>
    </Box>
  ) : (
    <Box sx={desktopActionsSx}>
      {showRefreshPreviewButton && (
        <RefreshPreviewButton isMobile={false} />
      )}
      {showShowIssuesButton && <ShowIssuesButton isMobile={false} />}
      {showEditButton && <EditButton isMobile={false} />}
      {showDownloadButton && <DownloadButton idx={idx} isMobile={false} />}
      {showUnlinkDocumentationButton && (
        <UnlinkDocumentationButton
          idx={idx}
          actions={actions}
          isMobile={false}
        />
      )}
      {showDrawButton && (
        <DrawButton setDrawMode={setDrawMode} isMobile={false} />
      )}
      {showRotateButton && (
        <RotateButton idx={idx} actions={actions} isMobile={false} />
      )}
      {showRemoveButton && (
        <RemoveButton idx={idx} actions={actions} isMobile={false} />
      )}
      {showGoToSourceIssueButton && (
        <GoToSourceIssueButton isMobile={false} />
      )}
      {showFiltersButton && (
        <IssueStateToggleButton isMobile={false} />
      )}
      {showFiltersButton && <FilterButton isMobile={false} />}
      {showExportToDocumentationButton && (
        <ExportToDocumentationButton isMobile={false} />
      )}
      {showGoToDocumentationFileButton && (
        <GoToDocumentationFileButton isMobile={false} />
      )}
      {showShareLinkButton && <ShareLinkButton isMobile={false} />}
    </Box>
  );
};

export default Actions;
